import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Spinner, Card, Tag, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Message } from '../../stores/ChatStoreMST';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import MessageContent from './MessageContent';
import './MessageList.css';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  isStreamingResponse: boolean;
  showDebugInfo: boolean;
  typingUsers: Set<string>;
}

const MessageList: React.FC<MessageListProps> = observer(({
  messages,
  isLoading,
  isStreamingResponse,
  showDebugInfo,
  typingUsers
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // HOOKS MUST BE AT TOP LEVEL
  const appStore = useAppStore();
  const { auth: authStore, theme: themeStore } = appStore!;  // Non-null assertion - MessageList only renders when authenticated
  const userName = authStore.user?.name || authStore.user?.email?.split('@')[0] || 'User';
  const userInitials = userName.slice(0, 2).toUpperCase();

  useEffect(() => {
    // Auto-scroll to bottom on new messages with a small offset so
    // the last message does not hide behind the composer
    const container = containerRef.current;
    if (!container) return;
    // Use rAF to wait for layout
    requestAnimationFrame(() => {
      const bottomPadding = 24; // small offset
      container.scrollTo({
        top: container.scrollHeight + bottomPadding,
        behavior: 'smooth'
      });
    });
  }, [messages.length, isStreamingResponse]);

  const renderMessage = (message: Message) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';
    const isError = message.metadata?.isError === true;
    
    return (
      <div
        key={message.id}
        className={`message-item ${message.role} ${isError ? 'message-error' : ''}`}
      >
        <div className="message-wrapper">
          {/* Avatar */}
          <div className="message-avatar">
            {isError ? (
              <div className="avatar avatar-error">
                <Icon icon={IconNames.ERROR} size={16} />
              </div>
            ) : message.role === 'user' ? (
              <div className="avatar avatar-user">
                {userInitials}
              </div>
            ) : message.role === 'assistant' ? (
              <div className="avatar avatar-ai">
                <Icon icon={IconNames.PREDICTIVE_ANALYSIS} size={16} />
              </div>
            ) : (
              <div className="avatar avatar-system">
                <Icon icon={IconNames.INFO_SIGN} size={14} />
              </div>
            )}
          </div>
          
          {/* Message Content Section */}
          <div className="message-body">
            <div className="message-header">
              <span className="message-role">
                {isError ? '⚠️ Error' :
                 message.role === 'assistant' ? 'AI' : 
                 message.role === 'user' ? userName : 
                 message.role}
              </span>
              {message.role === 'assistant' && message.modelUsed && (
                <Tag minimal className={`message-model ${isError ? 'bp5-intent-danger' : ''}`}>
                  {message.modelUsed}
                </Tag>
              )}
              <span className="message-time">
                {message.createdAt ? (
                  (() => {
                    try {
                      const date = new Date(message.createdAt);
                      if (isNaN(date.getTime())) {
                        return '';
                      }
                      return date.toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      });
                    } catch {
                      return '';
                    }
                  })()
                ) : ''}
              </span>
            </div>
            
            <Card className={`message-content ${isError ? 'message-content-error' : ''}`} elevation={0}>
              <MessageContent
                content={message.content}
                htmlContent={message.htmlContent}
                mentions={message.mentions}
                attachments={message.attachments}
                onMentionClick={(mention) => {
                  console.log('Mention clicked:', mention);
                  // TODO: Navigate to the mentioned entity
                }}
              />
              
              {showDebugInfo && message.totalTokens > 0 && (
                <div className="message-debug">
                  <span>Tokens: {message.totalTokens}</span>
                  {message.promptTokens > 0 && (
                    <span> (Prompt: {message.promptTokens}, Completion: {message.completionTokens})</span>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="message-list" ref={containerRef}>
      {messages.length === 0 && !isLoading ? (
        <div className="empty-conversation">
          <p>Start a conversation by typing a message below.</p>
        </div>
      ) : (
        <>
          {messages.map(renderMessage)}
          
          {isStreamingResponse && (
            <div className="message-item assistant streaming">
              <div className="message-header">
                <span className="message-role">AI</span>
                <span className="streaming-indicator">Generating...</span>
              </div>
              <Card className="message-content" elevation={0}>
                <div className="streaming-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </Card>
            </div>
          )}
          
          {typingUsers.size > 0 && (
            <div className="typing-indicator">
              {Array.from(typingUsers).map(userId => (
                <span key={userId}>User is typing...</span>
              ))}
            </div>
          )}
        </>
      )}
      
      {isLoading && (
        <div className="loading-messages">
          <Spinner size={Spinner.SIZE_SMALL} />
          <span>Loading messages...</span>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
});

export default MessageList;
